const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "http://localhost:8787";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/**
 * The access token lives in memory only — never localStorage, which is readable
 * by any injected script. Durability comes from the httpOnly refresh cookie.
 * The same reasoning applies here as in the customer dashboard, and matters
 * more: these sessions can suspend accounts and rotate server credentials.
 */
let accessToken: string | null = null;
const subscribers = new Set<(token: string | null) => void>();

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
  for (const notify of subscribers) notify(token);
}

export function onAccessTokenChange(listener: (token: string | null) => void): () => void {
  subscribers.add(listener);
  return () => subscribers.delete(listener);
}

let refreshInFlight: Promise<string | null> | null = null;

/** Concurrent 401s share one refresh round trip instead of racing. */
export function refreshSession(): Promise<string | null> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${API_URL}/v1/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });
      if (!response.ok) {
        setAccessToken(null);
        return null;
      }
      const data = (await response.json()) as { accessToken: string };
      setAccessToken(data.accessToken);
      return data.accessToken;
    } catch {
      setAccessToken(null);
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  /**
   * A multipart body, for the one endpoint that takes a file: an attachment on
   * a support ticket. The content type is deliberately left unset — the browser
   * writes the boundary into it, and a hand-set one produces a request nothing
   * can parse.
   */
  form?: FormData;
  signal?: AbortSignal;
  /**
   * The operator's authenticator code, for the requests that destroy a
   * tenant's workloads. Sent as `x-maxlayer-otp`; the API answers 403
   * `totp_required` without it or with a wrong one — 403 and not 401, so
   * the refresh-and-resend below never repeats an attempt nobody typed.
   */
  otp?: string;
  /** Skip the refresh-and-retry dance (used by the auth endpoints themselves). */
  anonymous?: boolean;
}

async function send(path: string, options: RequestOptions, token: string | null): Promise<Response> {
  const headers = new Headers();
  if (options.body !== undefined) headers.set("content-type", "application/json");
  if (token) headers.set("authorization", `Bearer ${token}`);
  if (options.otp) headers.set("x-maxlayer-otp", options.otp);

  const body = options.form ?? (options.body !== undefined ? JSON.stringify(options.body) : undefined);

  return fetch(`${API_URL}${path}`, {
    method: options.method ?? "GET",
    headers,
    credentials: "include",
    ...(body !== undefined ? { body } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response = await send(path, options, options.anonymous ? null : accessToken);

  if (response.status === 401 && !options.anonymous) {
    const renewed = await refreshSession();
    if (renewed) response = await send(path, options, renewed);
  }

  if (!response.ok) throw await toApiError(response);
  if (response.status === 204) return undefined as T;

  return (await response.json()) as T;
}

/**
 * Uploads one file. A fresh `FormData` per attempt, because a `File` is read
 * once by `fetch` and the 401-refresh-retry below would otherwise send an empty
 * body on the second try.
 */
export async function apiUpload<T>(path: string, file: File): Promise<T> {
  const build = () => {
    const form = new FormData();
    form.append("file", file);
    return form;
  };

  let response = await send(path, { method: "POST", form: build() }, accessToken);

  if (response.status === 401) {
    const renewed = await refreshSession();
    if (renewed) response = await send(path, { method: "POST", form: build() }, renewed);
  }

  if (!response.ok) throw await toApiError(response);
  return (await response.json()) as T;
}

/**
 * Fetches a file as a blob, with the operator's token.
 *
 * An attachment cannot be a plain link: the API authorizes with a bearer token
 * held in memory, and an anchor sends no header. The console turns the blob
 * into an object URL — which is also how an image preview happens without a
 * customer's file ever being served as a document on this origin.
 */
export async function apiBlob(path: string): Promise<Blob> {
  let response = await send(path, {}, accessToken);

  if (response.status === 401) {
    const renewed = await refreshSession();
    if (renewed) response = await send(path, {}, renewed);
  }

  if (!response.ok) throw await toApiError(response);
  return response.blob();
}

/** Builds `?a=1&b=2`, dropping empty values so filters can be cleared. */
export function query(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const rendered = search.toString();
  return rendered ? `?${rendered}` : "";
}

async function toApiError(response: Response): Promise<ApiError> {
  let code = "unknown_error";
  let message = `Request failed with status ${response.status}`;
  let details: unknown;

  try {
    const body = (await response.json()) as {
      error?: { code?: string; message?: string; details?: unknown };
    };
    if (body.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
      details = body.error.details;
    }
  } catch {
    // Non-JSON error body; keep the status-derived message.
  }

  return new ApiError(response.status, code, message, details);
}

export { API_URL };
